import React, { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { faFileAlt } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";

import {
	MenuItemToBeDisabledForExternalWorkspaces,
	MenuItemTooltip,
	useGetPropsForExternalWorkspaces,
} from "~/meetings/MeetingHeader/MoreActionsMenu/MenuItemTooltip";
import { ApplyTemplateModal } from "~/noteTemplate/ApplyModal";
import { useNoteTemplateModal } from "~/noteTemplate/modal";
import { useModals } from "~/ui/modals";

import type { ApplyTemplate_note$key } from "~/__generated__/ApplyTemplate_note.graphql";

const noteFragment = graphql`
	fragment ApplyTemplate_note on Note {
		id
		version
		isLocked
		stream {
			isExternal
		}
		...MenuItemTooltip_useGetPropsForExternalWorkspaces_note
	}
`;

export default function ApplyTemplate({ note: noteKey }: { note: ApplyTemplate_note$key }) {
	const modals = useModals();

	const note = useFragment(noteFragment, noteKey);

	const openTemplateModal = useNoteTemplateModal();
	const onOpenTemplateModal = useCallback(() => {
		openTemplateModal({
			mode: "browse",
			noteId: note.id,
			beforeSubmit: async templateId => {
				if (!templateId) return true;
				const shouldClose = await modals.open<boolean>(({ close }) => (
					<ApplyTemplateModal noteId={note.id} noteVersion={note.version} templateId={templateId} close={close} />
				));
				return shouldClose ?? false;
			},
		});
	}, [openTemplateModal, modals, note.id, note.version]);

	const tooltipPropsForExternalWorkspaces = useGetPropsForExternalWorkspaces(note);

	return (
		<MenuItemToBeDisabledForExternalWorkspaces
			icon={faFileAlt}
			onSelect={onOpenTemplateModal}
			disabled={note.isLocked}
			data-test-id="note-header-dropdown-applytemplate-item"
			{...tooltipPropsForExternalWorkspaces}
		>
			<MenuItemTooltip disabled={!note.isLocked} content="Cannot apply templates to locked notes.">
				<div>Apply a note template…</div>
			</MenuItemTooltip>
		</MenuItemToBeDisabledForExternalWorkspaces>
	);
}
